import { useEndpointFetch } from '../network/useEndpointFetch';

export interface StageStatus {
  key: string;
  name: string;
  order: number;
  percentage: number;
  completed: boolean;
  completedAt: string | null;
  data: Record<string, unknown> | null;
}

export interface SetupProgress {
  stagesCompleted: string[];
  stageData: Record<string, Record<string, unknown> | null>;
  questionnaireAnswers: Record<string, unknown> | null;
  currentStage: string | null;
  progressPercentage: number;
}

// Raw response from backend
export interface GetSetupProgressRawResponse {
  setupDialogViewed: boolean;
  currentStage: string | null;
  progressPercentage: number;
  isComplete: boolean;
  stages: StageStatus[];
  questionnaireAnswers: Record<string, unknown> | null;
}

// Transformed response for context
export interface GetSetupProgressResponse {
  setupDialogViewed: boolean;
  setupProgress: SetupProgress | null;
}

// Transform raw response to expected format
export function transformSetupProgressResponse(raw: GetSetupProgressRawResponse): GetSetupProgressResponse {
  const stagesCompleted = raw.stages.filter((s) => s.completed).map((s) => s.key);
  const stageData: Record<string, Record<string, unknown> | null> = {};
  raw.stages.forEach((s) => {
    if (s.completed && s.data) {
      stageData[s.key] = s.data;
    }
  });

  return {
    setupDialogViewed: raw.setupDialogViewed,
    setupProgress: {
      stagesCompleted,
      stageData,
      questionnaireAnswers: raw.questionnaireAnswers,
      currentStage: raw.currentStage,
      progressPercentage: raw.progressPercentage,
    },
  };
}

export interface CompleteStageValues {
  stageKey: string;
  data?: Record<string, unknown>;
  questionnaireAnswers?: Record<string, unknown>;
}

export interface CompleteStageResponse {
  success: boolean;
  stageKey: string;
  setupProgress: SetupProgress;
}

export interface MarkDialogViewedResponse {
  success: boolean;
}

export const useGetSetupProgressFetch = () => {
  return useEndpointFetch<Record<string, never>, GetSetupProgressRawResponse>('/tm/trial/setup-progress', {
    method: 'get',
  });
};

export const useCompleteStageFetch = () => {
  return useEndpointFetch<CompleteStageValues, CompleteStageResponse>('/tm/trial/complete-stage');
};

export const useMarkDialogViewedFetch = () => {
  return useEndpointFetch<Record<string, never>, MarkDialogViewedResponse>('/tm/trial/mark-dialog-viewed');
};
